const MarkingUtils = {}

MarkingUtils.calculateRating = (ratings = [], achievedLevel, yearLevel) => {
  const ageExpectedRatings = (ratings || [])
    .filter((item) => item.type === 'age-expected-scale')
    .sort((a, b) => a.scaleAE - b.scaleAE)

  if (!achievedLevel || !yearLevel) return null

  const getLevelPosition = (level) => {
    if (!level) return null

    const value = Number(level.code)
    return Number.isFinite(value) ? value : level.position
  }

  const achievedLevelPosition = getLevelPosition(achievedLevel)
  const yearLevelPosition = getLevelPosition(yearLevel)

  if (achievedLevelPosition == null || yearLevelPosition == null) return null
  const currentDate = new Date()
  const month = currentDate.getMonth() + 1
  // beginning
  let period = 1
  if (month >= 5 && month <= 11) {
    // middle
    period = 0.5
  } else if (month >= 12) {
    // end
    period = 0
  }
  const expectedLevel = yearLevelPosition + period
  const difference = achievedLevelPosition - expectedLevel
  return ageExpectedRatings.find((rating) => difference >= Number(rating.threshold))
}

MarkingUtils.sortGranularSteps = (levels = [], granularSteps = []) => {
  let sorted = []
  levels
    .sort((a, b) => b.position - a.position)
    .forEach((level) => {
      const gs = granularSteps
        .filter((step) => step.levelId === level._id)
        .map((step) => {
          const position = parseInt(step.id.split('gs-')[1])
          return {
            ...step,
            position,
            levelName: level.name
          }
        })
        .sort((a, b) => b.position - a.position)

      sorted = [...sorted, ...gs]
    })

  return sorted
}

MarkingUtils.setMarking = (levels = [], granularSteps = [], minMarking, maxMarking, completed, maxLevel) => {
  let newGranularSteps = MarkingUtils.sortGranularSteps(levels, [...granularSteps])
  const lvls = levels
    .sort((a, b) => b.position - a.position)
    .map((level) => String(level._id))
    .join(',')

  if (maxLevel) {
    const levelId = maxLevel.levelId
    const below = lvls.split(`${levelId},`)
    let itemsBelow = below[1] ? below[1].split(',') : []

    const above = lvls.split(`,${levelId}`)
    let itemsAbove = above[0] ? above[0].split(',') : []
    newGranularSteps = newGranularSteps.map((gs) => {
      if (gs.levelId === maxLevel.levelId) {
        if (gs.id === maxLevel.id)
          return {
            ...gs,
            [completed]: maxLevel.completed
          }
        if (gs.isLevel) {
          return {
            ...gs,
            [completed]: maxMarking
          }
        } else {
          if (maxLevel.completed !== maxMarking && maxLevel.isLevel) {
            return {
              ...gs,
              [completed]: minMarking
            }
          }
        }
      }

      if (itemsBelow.includes(String(gs.levelId))) {
        return {
          ...gs,
          [completed]: maxMarking
        }
      }

      if (itemsAbove.includes(String(gs.levelId))) {
        return {
          ...gs,
          [completed]: minMarking
        }
      }

      return gs
    })
  }

  return newGranularSteps
}

MarkingUtils.lcgs = (granularSteps, studentGranularSteps, lcId, marking = [], ulId, student, levels) => {
  const newGranularSteps = MarkingUtils.sortGranularSteps(levels, [...granularSteps])
  const minMarkig = marking?.find((item) => item.minValue) || {}
  let markingTeacherIsMaxLevel = null
  let markingStudentIsMaxLevel = null

  return newGranularSteps.map((step) => {
    const find = studentGranularSteps?.find((item) => item.id === step.id)
    if (find) {
      const completedMarkingTeacher = marking.find(
        (mark) => String(mark._id) === String(find.completedTeacher)
      )
      const completedMarkingStudent = marking.find(
        (mark) => String(mark._id) === String(find.completedStudent)
      )
      const logTeacher = student?.logMarkingTeacher?.find(
        (log) =>
          log.lcId === lcId &&
          log.ulId === ulId &&
          log?.to.some((from) => from.id === step.id && from.completed !== minMarkig._id)
      )
      const logStudent = student?.logMarkingStudent?.find(
        (log) =>
          log.lcId === lcId &&
          log.ulId === ulId &&
          log?.to.some((from) => from.id === step.id && from.completed !== minMarkig._id)
      )
      const logs = []
      if (logTeacher && !markingTeacherIsMaxLevel && find.definition) {
        if (find.completedTeacher !== minMarkig._id) {
          markingTeacherIsMaxLevel = step.levelId
        }
      }
      if (logStudent && !markingStudentIsMaxLevel && find.definition) {
        if (find.completedStudent !== minMarkig._id) {
          markingStudentIsMaxLevel = step.levelId
        }
      }
      let markingTeacherBy = logTeacher?.userName || logTeacher?.userCode
      let markingTeacherByCode = logTeacher?.userCode
      let markingTeacherByPhoto = `${logTeacher?.userId || ''}.jpg`
      let markingTeacherClosedBy = null
      if (logTeacher?.type === 'close') {
        markingTeacherClosedBy = logTeacher?.userName || logTeacher?.userCode
        const codes = new Set([...(logTeacher?.notes?.map((note) => note.userName || note.userCode) || [])])
        markingTeacherBy = [...codes].filter((c) => !!c).join(', ')
        markingTeacherByCode = [...codes].filter((c) => !!c).join(', ')
      }
      logs.push({
        _id: `${lcId}-${step.id}-${logTeacher?._id}`,
        marking: find?.completedTeacher,
        markingValue: find?.completedTeacher ? completedMarkingTeacher?.value || minMarkig.value : 0,
        markingName: completedMarkingTeacher?.name,
        markingPrimaryColor: completedMarkingTeacher?.primaryColor,
        markingSecondaryColor: completedMarkingTeacher?.secondaryColor,
        markingBy: markingTeacherBy,
        markingByCode: markingTeacherByCode,
        markingByPhoto: markingTeacherByPhoto,
        markingClosedBy: markingTeacherClosedBy,
        markingDate: logTeacher?.date,
        markingType: 'completedTeacher',
        exists: !!find?.completedTeacher,
        markingIsMaxLevel: markingTeacherIsMaxLevel === step.levelId,
        markingMaxLevel: markingTeacherIsMaxLevel,
        definition: find?.definition,
        gsId: step.id
      })
      let markingStudentBy = logStudent?.userName || logStudent?.userCode
      let markingStudentByCode = logStudent?.userCode
      let markingStudentByPhoto = `${logStudent?.userId || ''}.jpg`
      let markingStudentClosedBy = null
      if (logStudent?.type === 'close') {
        markingStudentClosedBy = logStudent?.userName || logStudent?.userCode
        const codes = new Set([...(logStudent?.notes?.map((note) => note.userName || note.userCode) || [])])
        markingTeacherBy = [...codes].filter((c) => !!c).join(', ')
        markingStudentByCode = [...codes].filter((c) => !!c).join(', ')
      }

      logs.push({
        _id: `${lcId}-${step.id}-${logTeacher?._id}`,
        marking: find?.completedStudent,
        markingValue: find?.completedStudent ? completedMarkingStudent?.value || minMarkig.value : 0,
        markingName: completedMarkingStudent?.name,
        markingPrimaryColor: completedMarkingStudent?.primaryColor,
        markingSecondaryColor: completedMarkingStudent?.secondaryColor,
        markingBy: markingStudentBy,
        markingByCode: markingStudentByCode,
        markingByPhoto: markingStudentByPhoto,
        markingClosedBy: markingStudentClosedBy,
        markingDate: logStudent?.date,
        markingType: 'completedStudent',
        exists: !!find?.completedStudent,
        markingIsMaxLevel: markingStudentIsMaxLevel === step.levelId,
        markingMaxLevel: markingStudentIsMaxLevel,
        gsId: step.id,
        definition: find?.definition
      })

      return {
        ...step,
        ...find,
        logs,
        completedTeacherValue: find?.completedTeacher
          ? find.definition
            ? completedMarkingTeacher?.value || minMarkig.value
            : 0
          : 0,
        completedStudentValue: find?.completedStudent
          ? find.definition
            ? completedMarkingStudent?.value || minMarkig.value
            : 0
          : 0
      }
    }
    return step
  })
}

MarkingUtils.updateLcsDataStudent = (levels = [], marking, student = [], ulLcs = [], ulId) => {
  return ulLcs.map((lc) => {
    const find = student?.lcs?.find(
      (item) => String(item._id) === String(lc._id) && String(item.ulId) === String(ulId)
    )
    if (find) {
      const granularSteps = MarkingUtils.lcgs(
        lc.granularSteps,
        find.granularSteps,
        lc._id,
        marking,
        ulId,
        student,
        levels
      )
      return {
        ...lc,
        ...find,
        granularSteps,
        completedTeacherValue: granularSteps.reduce((acc, step) => acc + step.completedTeacherValue, 0),
        completedStudentValue: granularSteps.reduce((acc, step) => acc + step.completedStudentValue, 0)
      }
    } else {
      const find2 = student?.lcs?.find((item) => String(item._id) === String(lc._id))
      if (find2) {
        const granularSteps = MarkingUtils.lcgs(
          lc.granularSteps,
          find2.granularSteps,
          lc._id,
          marking,
          ulId,
          student,
          levels
        )
        return {
          ...lc,
          ...find2,
          granularSteps,
          completedTeacherValue: granularSteps.reduce((acc, step) => acc + step.completedTeacherValue, 0),
          completedStudentValue: granularSteps.reduce((acc, step) => acc + step.completedStudentValue, 0)
        }
      }
      return lc
    }
  })
}

MarkingUtils.mapLogs = (LcsData, ulId, logs = [], ulStatus) => {
  const lcIds = LcsData.map((lc) => lc._id)
  const newLogs = logs.filter((log) => lcIds.includes(String(log.lcId)) && String(log.ulId) === String(ulId))
  let llogs = logs.filter((log) => !newLogs.some((nlog) => nlog._id === log._id))
  if (ulStatus === 'inactive') {
    return [...newLogs, ...llogs].sort((a, b) => new Date(b.date) - new Date(a.date) && b._id - a.date)
  }

  return [...newLogs].sort((a, b) => new Date(b.date) - new Date(a.date) && b._id - a._id)
}

MarkingUtils.mapStudentsGranularSteps = (currentCurriculum, student, studentMaestro = [], ul = []) => {
  const { marking, levels } = currentCurriculum
  let lcs = MarkingUtils.updateLcsDataStudent(levels, marking, ul, ul.lcs, ul._id)
  let logMarkingStudent = []
  let logMarkingTeacher = []
  if (studentMaestro) {
    lcs = MarkingUtils.updateLcsDataStudent(
      levels,
      marking,
      studentMaestro,
      lcs,
      ul.status === 'active' ? ul._id : ''
    )
    logMarkingStudent = MarkingUtils.mapLogs(lcs, ul._id, studentMaestro.logMarkingStudent, ul.status)
    logMarkingTeacher = MarkingUtils.mapLogs(lcs, ul._id, studentMaestro.logMarkingTeacher, ul.status)
  }
  return {
    ...student,
    lcs,
    logMarkingTeacher,
    logMarkingStudent,
    completedTeacherValue: lcs.reduce((acc, step) => acc + step.completedTeacherValue, 0),
    completedStudentValue: lcs.reduce((acc, step) => acc + step.completedStudentValue, 0)
  }
}

MarkingUtils.mapGranularStepsLearningMatrix = (currentCurriculum, student, studentMaestro = []) => {
  const { marking, levels } = currentCurriculum
  let lcs = []

  if (studentMaestro) {
    lcs = studentMaestro.lcs?.map((lc) => {
      const gs = lc.granularSteps.filter((step) => step.isLevel)
      const granularSteps = MarkingUtils.lcgs(
        gs,
        gs,
        lc._id,
        marking,
        lc.ulId,
        { ...studentMaestro, ...student },
        levels
      )
      return {
        ...lc,
        ...find,
        granularSteps,
        completedTeacherValue: granularSteps.reduce((acc, step) => acc + step.completedTeacherValue, 0),
        completedStudentValue: granularSteps.reduce((acc, step) => acc + step.completedStudentValue, 0)
      }
    })
  }

  return {
    ...student,
    lcs
  }
}

/**
 * This funtcion generate log marking comparing new marking to old marking
 * @param {Array} oldGS old granularSteps marked
 * @param {Array} newGS new granularSteps marked
 * @param {String} completed type complete markingTeacher || markingStudent
 * @param {String} minValue min value for marking
 */
MarkingUtils.generateFromToLogMarking = (oldGS, newGS, completed, minValue = 0) => {
  let from = []
  let to = []
  let gsold = oldGS
  let gsnew = newGS
  gsold.forEach((oldul, i) => {
    const newul = gsnew[i]

    if (String(oldul[completed]) !== String(newul[completed])) {
      from.push({
        id: oldul.id,
        completed: oldul[completed] || minValue,
        levelId: oldul.levelId,
        isLevel: oldul.id.indexOf('gs-0') > -1
      })
      to.push({
        id: newul.id,
        completed: newul[completed] || minValue,
        levelId: newul.levelId,
        isLevel: newul.id.indexOf('gs-0') > -1
      })
    }
  })

  from = from.filter((item, i) => item.completed !== to[i]?.completed)
  to = to.filter((item, i) => item.id === from[i]?.id)

  return { from, to }
}

/**
 * Get color label for granularSteps
 *
 */
MarkingUtils.getColorLabel = (marking, maxLevel, isLevel, level) => {
  const maxValue = marking.find((item) => item.maxValue)
  const minValue = marking.find((item) => item.minValue)
  let data = {
    color: minValue?.secondaryColor || '#000',
    backgroundColor: 'var(--background)',
    outline: minValue?.secondaryColor || '#000'
  }
  if (isLevel) {
    data.color = minValue?.primaryColor || '#f2f7fc'
    data.backgroundColor = minValue?.secondaryColor || '#000'
    data.outline = 'var(--background)'
  }

  if (maxLevel) {
    if (maxLevel?.levelPosition === level.position) {
      data.color = maxLevel?.secondaryColor || '#000'
      data.backgroundColor = 'var(--background)'
      data.outline = maxLevel?.secondaryColor || '#000'
      if (isLevel) {
        data.color = maxLevel?.primaryColor || '#f2f7fc'
        data.backgroundColor = maxLevel?.secondaryColor || '#000'
        data.outline = 'var(--background)'
      }
    } else if (maxLevel?.levelPosition > level.position) {
      data.color = maxValue?.secondaryColor || '#000'
      data.backgroundColor = 'var(--background)'
      data.outline = maxValue?.secondaryColor || '#000'
      if (isLevel) {
        data.color = maxValue?.primaryColor || '#f2f7fc'
        data.backgroundColor = maxValue?.secondaryColor || '#000'
        data.outline = 'var(--background)'
      }
    }
  }

  return data
}

/**
 * Get color label for granularSteps
 *
 */
MarkingUtils.getMarkingLevel = (marking = [], levels = [], lcs, completed) => {
  let gss = {}
  const maxMarkig = marking.find((item) => item.maxValue) || {}

  lcs.forEach((lc) => {
    lc.granularSteps.forEach((step) => {
      if (step.definition != '') {
        if (!gss[step.id]) {
          const findLevel = levels.find((lvl) => String(lvl._id) === String(step.levelId))
          gss[step.id] = {
            [completed]: step[completed],
            completed: step[completed],
            levelId: step.levelId,
            levelName: findLevel ? findLevel.name : '',
            levelPosition: findLevel ? findLevel.position : 0,
            isLevel: step.isLevel,
            count: 0,
            length: 0
          }
        }
        gss[step.id].length += 1
        if (String(step[completed]) === String(maxMarkig._id)) {
          gss[step.id].count += 1
        }
      }
    })
  })

  // get max marking level
  gss = Object.values(gss).filter((item) => item.count >= item.length / 2)
  gss = gss.sort((a, b) => b.levelPosition - a.levelPosition)

  const maxLevel = gss[0] || null
  if (maxLevel) {
    return {
      ...maxLevel,
      primaryColor: maxMarkig.primaryColor,
      secondaryColor: maxMarkig.secondaryColor,
      name: maxMarkig.name
    }
  }

  return maxLevel
}

export default MarkingUtils
